/**
 * Slide-in log drawer from the right edge.
 *
 * The drawer renders fixed-positioned over the board. Click outside or the
 * close button to dismiss. List of entries (newest at the bottom) with round
 * number prefix and player attribution.
 */
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { GameLogEntry } from "@/types/game";

interface LogDrawerProps {
  open: boolean;
  log: GameLogEntry[];
  onClose: () => void;
}

export function LogDrawer({ open, log, onClose }: LogDrawerProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [open, log.length]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close log"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-80 max-w-full flex-col border-l border-[#2a2f3a] bg-[#1a1d24] shadow-2xl">
        <header className="flex items-center justify-between border-b border-[#2a2f3a] px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-[#e2e8f0]">Match Log</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-[#e2e8f0]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3 font-mono text-[11px] leading-relaxed">
          {log.length === 0 ? (
            <p className="text-[#64748b]">No events yet.</p>
          ) : (
            <ul className="space-y-1">
              {log.map((l, i) => (
                <li key={i}>
                  <span className="text-[#64748b]">[R{l.round}]</span>{" "}
                  <span className="text-amber-soft">{l.player}:</span>{" "}
                  <span className="text-[#cbd5e1]">{l.message}</span>
                </li>
              ))}
              <div ref={bottomRef} />
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
